import React, { useEffect, useRef, useState } from 'react';
import { SystemConfig, SystemConfigUpdatePayload } from '@/types';
import {
  Activity,
  BookOpen,
  CreditCard,
  FileText,
  Fingerprint,
  Layout,
  Loader2,
  Mail,
  MessageSquare,
  Save,
  Send
} from 'lucide-react';
import { showConfirmModal } from '@shared/components/common/AlertModal';
import { api } from '@/services/api';
import { buildUpdateSystemConfigPayload } from '@/services/apiPayloadUtils';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { validateEmail } from '@/lib/validators';
import { formatTaxRateMappingsText } from './settingsShared';
import { GeneralSection } from './GeneralSection';
import { CommunicationsSection } from './CommunicationsSection';
import { PaymentsSection } from './PaymentsSection';
import { EmailTemplatesSection } from './EmailTemplatesSection';
import { SmsTemplatesSection } from './SmsTemplatesSection';
import { VerificationSection } from './VerificationSection';
import { CertificateSection } from './CertificateSection';
import { AuditLogsSection } from './AuditLogsSection';

type ConfigSubTab =
  | 'general'
  | 'communications'
  | 'payments'
  | 'email_content'
  | 'sms_content'
  | 'verification'
  | 'templates'
  | 'terms'
  | 'audit_logs';

type NavigationItem = {
  id: ConfigSubTab;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  group: string;
};

const NAVIGATION_ITEMS: NavigationItem[] = [
  { id: 'general', label: 'General Branding', icon: Layout, group: 'Application' },
  { id: 'terms', label: 'Booking Policy', icon: BookOpen, group: 'Application' },
  { id: 'audit_logs', label: 'Audit Logs', icon: Activity, group: 'Security' },
  { id: 'communications', label: 'Gateway Settings', icon: Mail, group: 'Communications' },
  { id: 'email_content', label: 'Email Templates', icon: Send, group: 'Communications' },
  { id: 'sms_content', label: 'SMS Templates', icon: MessageSquare, group: 'Communications' },
  { id: 'payments', label: 'Billing Integrations', icon: CreditCard, group: 'Billing' },
  { id: 'verification', label: 'Verification UI', icon: Fingerprint, group: 'Clinical Output' },
  { id: 'templates', label: 'Certificate Layout', icon: FileText, group: 'Clinical Output' }
];

const NAV_GROUPS = ['Application', 'Security', 'Communications', 'Billing', 'Clinical Output'];

interface SettingsTabProps {
  systemConfig?: SystemConfig;
  onUpdateSystemConfig?: (config: SystemConfigUpdatePayload) => void;
  canEdit: boolean;
}

export const SettingsTab: React.FC<SettingsTabProps> = ({
  systemConfig,
  onUpdateSystemConfig,
  canEdit
}) => {
  const [config, setConfig] = useState<SystemConfig | null>(systemConfig || null);
  const [activeSubTab, setActiveSubTab] = useState<ConfigSubTab>('general');
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [sageTaxMappingsText, setSageTaxMappingsText] = useState('');
  const [isConnectingSage, setIsConnectingSage] = useState(false);
  const [pendingSageOauthState, setPendingSageOauthState] = useState<string | null>(null);
  const sageOauthPopupRef = useRef<Window | null>(null);

  const readOnly = !canEdit;
  const disableInputs = readOnly && activeSubTab !== 'audit_logs';

  useEffect(() => {
    if (systemConfig) {
      setConfig(systemConfig);
      setHasChanges(false);
    }
  }, [systemConfig]);

  useEffect(() => {
    if (!config?.sage) return;
    setSageTaxMappingsText(formatTaxRateMappingsText(config.sage.taxRateMappings || {}));
  }, [config?.sage?.taxRateMappings]);

  useEffect(() => {
    const handler = async (event: MessageEvent) => {
      const payload = event?.data;
      if (!payload || payload.type !== 'sage:oauth' || !pendingSageOauthState) return;
      if (payload.state !== pendingSageOauthState) return;
      if (sageOauthPopupRef.current && event.source && event.source !== sageOauthPopupRef.current)
        return;

      setPendingSageOauthState(null);
      setIsConnectingSage(false);
      try {
        if (sageOauthPopupRef.current && !sageOauthPopupRef.current.closed) {
          sageOauthPopupRef.current.close();
        }
      } catch (_err) {
        // ignore popup close errors
      } finally {
        sageOauthPopupRef.current = null;
      }

      if (!payload.success) {
        setMessage({
          type: 'error',
          text: String(payload.error || 'Sage OAuth connection failed.')
        });
        return;
      }
      try {
        const freshConfig = await api.getSystemConfig();
        setConfig(freshConfig);
        setHasChanges(false);
        setMessage({
          type: 'success',
          text: String(payload.message || 'Sage account connected successfully.')
        });
        setTimeout(() => setMessage(null), 4000);
      } catch (error: unknown) {
        setMessage({
          type: 'error',
          text: normalizeErrorMessage(error, 'Sage connected, but failed to refresh settings.')
        });
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [pendingSageOauthState]);

  useEffect(() => {
    if (!isConnectingSage || !pendingSageOauthState) return;
    const intervalId = window.setInterval(() => {
      const popup = sageOauthPopupRef.current;
      if (popup && popup.closed) {
        sageOauthPopupRef.current = null;
        setIsConnectingSage(false);
        setPendingSageOauthState(null);
        setMessage({ type: 'error', text: 'Sage connection window was closed before completion.' });
        setTimeout(() => setMessage(null), 4000);
      }
    }, 500);
    return () => window.clearInterval(intervalId);
  }, [isConnectingSage, pendingSageOauthState]);

  const updateConfig = (newConfig: SystemConfig) => {
    if (readOnly) return;
    setConfig(newConfig);
    setHasChanges(true);
  };

  const bookingAuditEmails = config?.templates.notificationSettings?.bookingAuditEmails || [];
  const invalidBookingAuditIndexes = new Set(
    bookingAuditEmails
      .map((email, index) => ({ email, index }))
      .filter(({ email }) => email.trim() && !validateEmail(email).valid)
      .map(({ index }) => index)
  );

  const handleSave = async () => {
    if (!config) return;
    if (
      bookingAuditEmails.some((e) => !e.trim()) ||
      bookingAuditEmails.some((e) => e.trim() && !validateEmail(e).valid)
    ) {
      setMessage({
        type: 'error',
        text: 'Please enter valid booking audit email addresses or remove invalid rows.'
      });
      return;
    }
    setIsSaving(true);
    setMessage(null);
    try {
      if (onUpdateSystemConfig) {
        const payload = buildUpdateSystemConfigPayload(config, systemConfig);
        if (Object.keys(payload).length === 0) {
          setHasChanges(false);
          setMessage({ type: 'success', text: 'No changes to save.' });
          setTimeout(() => setMessage(null), 3000);
          return;
        }
        onUpdateSystemConfig(payload);
        setMessage({ type: 'success', text: 'Settings saved successfully.' });
        setHasChanges(false);
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (e: unknown) {
      setMessage({
        type: 'error',
        text: normalizeErrorMessage(e, 'Failed to save configuration.')
      });
    } finally {
      setIsSaving(false);
    }
  };

  const connectSageWithOauth = async () => {
    if (!config || readOnly) return;
    if (hasChanges) {
      setMessage({
        type: 'error',
        text: 'Save your current Sage settings first, then connect with OAuth.'
      });
      return;
    }
    setIsConnectingSage(true);
    setMessage(null);
    try {
      const result = await api.startSageOauthConnection();
      const popup = window.open(
        result.authorizationUrl,
        'sage-oauth-connect',
        'popup=yes,width=640,height=780,resizable=yes,scrollbars=yes'
      );
      if (!popup) throw new Error('Popup was blocked. Please allow popups and try again.');
      sageOauthPopupRef.current = popup;
      setPendingSageOauthState(result.state);
      setMessage({
        type: 'success',
        text: 'Sage sign-in window opened. Complete authorization to finish connection.'
      });
    } catch (error: unknown) {
      setIsConnectingSage(false);
      setPendingSageOauthState(null);
      setMessage({
        type: 'error',
        text: normalizeErrorMessage(error, 'Failed to start Sage OAuth connection.')
      });
    }
  };

  const handleRestoreCertDefaults = async () => {
    if (!config) return;
    const confirmed = await showConfirmModal(
      'Restore default certificate templates? Current edits will be lost.',
      {
        title: 'Restore Defaults',
        confirmText: 'Restore',
        cancelText: 'Cancel',
        isDestructive: true
      }
    );
    if (!confirmed) return;
    try {
      const defaults = await api.getDefaultSystemConfig();
      updateConfig({
        ...config,
        templates: {
          ...config.templates,
          certificateTemplate: {
            ...(defaults.templates?.certificateTemplate || config.templates.certificateTemplate)
          }
        }
      });
    } catch (e: unknown) {
      setMessage({
        type: 'error',
        text: normalizeErrorMessage(e, 'Failed to load default templates.')
      });
    }
  };

  if (!config)
    return <div className="p-20 text-center text-slate-400">Loading system configuration...</div>;

  return (
    <div className="animate-fade-in pb-20">
      {message && (
        <div
          className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-xl shadow-xl text-sm font-bold ${message.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}
        >
          {message.text}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-8 items-start">
        <aside className="w-full lg:w-64 shrink-0 space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden p-2">
            {NAV_GROUPS.map((group) => (
              <div key={group} className="mb-4 last:mb-0">
                <p className="px-3 py-2 text-[10px] font-black uppercase text-slate-400 tracking-widest">
                  {group}
                </p>
                <div className="space-y-0.5">
                  {NAVIGATION_ITEMS.filter((i) => i.group === group).map((item) => (
                    <button
                      key={item.id}
                      onClick={() => setActiveSubTab(item.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-bold transition-all ${activeSubTab === item.id ? 'bg-teal-600 text-white shadow-lg shadow-teal-600/20' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}
                    >
                      <item.icon
                        className={`w-4 h-4 ${activeSubTab === item.id ? 'text-white' : 'text-slate-400'}`}
                      />
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {canEdit && (
            <div className="p-1">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className={`w-full py-4 rounded-2xl font-black flex items-center justify-center gap-3 disabled:opacity-50 shadow-xl transition-all active:scale-95 text-xs uppercase tracking-widest ${hasChanges ? 'bg-teal-600 hover:bg-teal-700 text-white animate-pulse' : 'bg-slate-900 hover:bg-slate-800 text-white'}`}
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {isSaving ? 'Saving...' : hasChanges ? 'Save Changes' : 'All Saved'}
              </button>
              {hasChanges && (
                <p className="text-[10px] text-orange-500 font-bold mt-2 text-center animate-bounce">
                  You have unsaved changes
                </p>
              )}
            </div>
          )}
        </aside>

        <div className="flex-1 w-full space-y-6">
          <fieldset disabled={disableInputs} className={disableInputs ? 'opacity-70' : ''}>
            <div className="bg-slate-50/50 rounded-3xl p-1 border border-slate-100 min-h-[600px]">
              {activeSubTab === 'general' && (
                <GeneralSection config={config} onUpdate={updateConfig} />
              )}

              {activeSubTab === 'terms' && (
                <div className="space-y-6 animate-fade-in">
                  <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                    <h4 className="font-bold text-lg text-slate-800 mb-2 flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-teal-600" /> Booking Policy
                    </h4>
                    <p className="text-xs text-slate-500 mb-6 font-medium">
                      This link is displayed during checkout. Use the first line for the link text
                      and the next line for the URL.
                    </p>
                    <textarea
                      className="w-full border rounded-2xl p-6 h-96 bg-slate-50 font-mono text-sm shadow-inner outline-none focus:ring-2 focus:ring-teal-500"
                      value={config.termsAndConditions}
                      onChange={(e) =>
                        updateConfig({ ...config, termsAndConditions: e.target.value })
                      }
                    />
                  </div>
                </div>
              )}

              {activeSubTab === 'audit_logs' && <AuditLogsSection />}

              {activeSubTab === 'communications' && (
                <CommunicationsSection
                  config={config}
                  onUpdate={updateConfig}
                  onSetMessage={setMessage}
                  invalidBookingAuditIndexes={invalidBookingAuditIndexes}
                />
              )}

              {activeSubTab === 'email_content' && (
                <EmailTemplatesSection config={config} onUpdate={updateConfig} canEdit={canEdit} />
              )}

              {activeSubTab === 'sms_content' && (
                <SmsTemplatesSection config={config} onUpdate={updateConfig} />
              )}

              {activeSubTab === 'payments' && (
                <PaymentsSection
                  config={config}
                  onUpdate={updateConfig}
                  sageTaxMappingsText={sageTaxMappingsText}
                  onSageTaxMappingsTextChange={setSageTaxMappingsText}
                  isConnectingSage={isConnectingSage}
                  onConnectSageOauth={connectSageWithOauth}
                />
              )}

              {activeSubTab === 'verification' && (
                <VerificationSection config={config} onUpdate={updateConfig} />
              )}

              {activeSubTab === 'templates' && (
                <CertificateSection
                  config={config}
                  onUpdate={updateConfig}
                  onRestoreDefaults={handleRestoreCertDefaults}
                  canEdit={canEdit}
                />
              )}
            </div>
          </fieldset>
        </div>
      </div>
    </div>
  );
};
